import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { fieldLabelSx } from '../common/formFieldStyles';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { getStorePaymentMethodKey } from '../../constants/StorePaymentMethod';
import { formatProductSaleItemLabel } from '../../utils/productSaleItemsUtils';
import { getProductSaleStatusChipSx } from '../../utils/productSaleStatusUtils';
import { BRAND } from '../../theme/brandTokens';

const destructiveActionButtonSx = {
  background: 'linear-gradient(135deg, #c62828 0%, #f44336 100%)',
  boxShadow: '0 10px 24px rgba(198, 40, 40, 0.18)',
  '&:hover': {
    background: 'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)',
  },
};

const DetailField = ({ label, value, children }) => (
  <Box sx={{ minWidth: 0, width: '100%' }}>
    <Typography variant="subtitle2" sx={fieldLabelSx}>
      {label}
    </Typography>
    {children || (
      <Typography variant="body1" sx={{ color: BRAND.text, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    )}
  </Box>
);

const ProductSaleDetailsDialog = ({
  open,
  sale,
  currency,
  canVoid = false,
  voiding = false,
  channelLabel,
  statusLabel,
  onClose,
  onVoid,
}) => {
  const { t, i18n } = useTranslation();
  const discount = sale
    ? Math.max(0, Math.round((Number(sale.totalAmount || 0) - Number(sale.amountReceived || 0)) * 100) / 100)
    : 0;
  const items = Array.isArray(sale?.items) ? sale.items : [];

  return (
    <ScrollableDialog
      open={open}
      onClose={voiding ? undefined : onClose}
      maxWidth="md"
      title={t('productsManagement.sales.detailsTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={voiding} color="inherit">
            {t('common.cancel')}
          </Button>
          {canVoid ? (
            <PrimaryActionButton onClick={onVoid} disabled={voiding} sx={destructiveActionButtonSx}>
              {t('productsManagement.sales.void')}
            </PrimaryActionButton>
          ) : null}
        </>
      )}
    >
      {sale ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <DetailField
              label={t('productsManagement.sales.table.status')}
            >
              <Chip size="small" label={statusLabel(sale.status)} sx={getProductSaleStatusChipSx(sale.status)} />
            </DetailField>
            <DetailField
              label={t('productsManagement.sales.table.date')}
              value={formatUtcDate(sale.soldAtUtc || sale.saleDate)}
            />
            <DetailField
              label={t('productsManagement.sales.table.professional')}
              value={sale.teamMemberName || t('productsManagement.sale.noProfessional')}
            />
            <DetailField
              label={t('productsManagement.sales.table.customer')}
              value={sale.customerName || t('productsManagement.sale.walkIn')}
            />
            <DetailField
              label={t('productsManagement.sales.table.channel')}
              value={channelLabel(sale.channel)}
            />
            <DetailField
              label={t('productsManagement.sales.table.paymentMethod')}
              value={sale.paymentMethod
                ? t(`storePaymentMethods.${getStorePaymentMethodKey(sale.paymentMethod)}`)
                : t('common.notProvided')}
            />
          </Box>

          <DetailField label={t('productsManagement.sales.table.items')}>
            {items.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) max-content',
                  columnGap: 2,
                  rowGap: 1,
                  width: '100%',
                  alignItems: 'baseline',
                }}
              >
                {items.map((item, index) => (
                  <React.Fragment key={`${item.productId || item.productName || 'item'}-${index}`}>
                    <Typography variant="body1" sx={{ color: BRAND.text, wordBreak: 'break-word' }}>
                      {formatProductSaleItemLabel(item, i18n.language)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: BRAND.accentText,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatCurrency(item.lineTotal, currency)}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            ) : (
              <Typography variant="body1" sx={{ color: BRAND.text }}>
                {sale.itemsSummary || t('common.notProvided')}
              </Typography>
            )}
          </DetailField>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <DetailField
              label={t('productsManagement.sales.table.total')}
              value={formatCurrency(sale.totalAmount, currency)}
            />
            <DetailField
              label={t('productsManagement.sales.table.collected')}
              value={formatCurrency(sale.amountReceived, currency)}
            />
            {discount > 0 ? (
              <DetailField
                label={t('productsManagement.sales.table.discount')}
                value={formatCurrency(discount, currency)}
              />
            ) : null}
            <DetailField
              label={t('productsManagement.sales.table.commission')}
              value={formatCurrency(sale.commissionAmount, currency)}
            />
          </Box>

          {sale.notes ? (
            <DetailField
              label={t('productsManagement.sale.notes')}
              value={sale.notes}
            />
          ) : null}

          {sale.sellerAssignedByTeamMemberName ? (
            <Typography variant="body2" color="text.secondary">
              {t('productsManagement.sales.sellerAssignedBy', {
                name: sale.sellerAssignedByTeamMemberName,
              })}
            </Typography>
          ) : null}
          {sale.customerLinesModifiedByTeamMemberName ? (
            <Typography variant="body2" color="text.secondary">
              {t('productsManagement.sales.customerLinesModifiedBy', {
                name: sale.customerLinesModifiedByTeamMemberName,
              })}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </ScrollableDialog>
  );
};

export default ProductSaleDetailsDialog;
